import { computed, Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';

export interface TenantBranding {
  /** The firm's name as clients know it. The portal is white-labelled, so this leads the header. */
  name: string;
  /** Square logo. Empty: the header draws the first letter of the name instead. */
  logoUrl: string;
  /**
   * Facts the firm can stand behind, shown on the welcome page instead of marketing claims.
   * Each one is optional: an unset fact is left out, never shown as a placeholder.
   */
  regulator?: string; // e.g. "Licensed by CySEC, licence 123/45"
  dataLocation?: string; // e.g. "Encrypted and stored in the EU"
  supportHours?: string; // e.g. "Mon–Fri, 9:00–17:00"
  supportEmail?: string;
}

const PLATFORM_NAME = 'Bizcombo';

/**
 * Who the client is onboarding with. Today this comes from environment.tenantBranding, because the
 * onboarding API still runs on one hard-coded tenant (OnboardingApiService.tenantId).
 *
 * TODO: load it from an anonymous customer-facing branding endpoint once tenants are resolved per
 * host, and call set() before the first render.
 */
@Injectable({ providedIn: 'root' })
export class TenantBrandingService {
  private readonly _branding = signal<TenantBranding>(environment.tenantBranding);

  readonly name = computed(() => this._branding().name || PLATFORM_NAME);
  readonly logoUrl = computed(() => this._branding().logoUrl);
  readonly initial = computed(() => this.name().trim().charAt(0).toUpperCase());
  /** "powered by Bizcombo" only makes sense when the firm isn't Bizcombo itself. */
  readonly isWhiteLabel = computed(() => this.name().toLowerCase() !== PLATFORM_NAME.toLowerCase());
  readonly regulator = computed(() => this._branding().regulator ?? '');
  readonly dataLocation = computed(() => this._branding().dataLocation ?? '');
  /** "Mon–Fri, 9:00–17:00 · help@firm.com", or whichever half is set. */
  readonly support = computed(() =>
    [this._branding().supportHours, this._branding().supportEmail].filter(Boolean).join(' · '),
  );

  set(branding: TenantBranding): void {
    this._branding.set(branding);
  }
}
